import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { EditBookingForm } from "@/components/shared/bookings/edit-booking-form";
import { RowsSelected } from "@/components/shared/tables/ActionBar";
import { Button } from "@/components/ui/button";
import { Shortcut } from "@/components/ui/shortcut";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface BulkEditActionBarProps {
  table: any;
  onAccountChange: (value: any) => void;
  onTaxCodeChange: (value: any) => void;
  onCostCenterChange: (value: any) => void;
  onDelete: () => Promise<void> | void;
}

const editLineItemFormSchema = z.object({
  accountId: z.string().max(255).nullable().optional(),
  taxCodeId: z.string().max(255).nullable().optional(),
  costCenterId: z.string().max(255).nullable().optional(),
});

type EditLineItemFrom = z.infer<typeof editLineItemFormSchema>;

export const BulkEditLineItemActionBar = ({
  table,
  onAccountChange,
  onTaxCodeChange,
  onCostCenterChange,
  onDelete,
}: BulkEditActionBarProps) => {
  const { t } = useTranslation();

  const handleSubmit = async ({
    accountId,
    taxCodeId,
    costCenterId,
  }: EditLineItemFrom) => {
    if (accountId !== undefined) {
      onAccountChange(accountId);
    }

    if (taxCodeId !== undefined) {
      onTaxCodeChange(taxCodeId);
    }

    if (costCenterId !== undefined) {
      onCostCenterChange(costCenterId);
    }
  };

  useHotkeys(["mod+d"], () => {
    onDelete();
  });

  return (
    <div className="flex flex-row gap-2 items-center">
      <RowsSelected
        totalRowsLength={table?.getSelectedRowModel()?.flatRows?.length}
        selectedRowsLength={table?.getSelectedRowModel()?.flatRows?.length}
        onDeselectAll={() => table.resetRowSelection(true)}
      />
      <EditBookingForm onSubmit={handleSubmit} />
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Button variant="outline" onClick={onDelete}>
              {t("delete")}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <Shortcut.ShortcutRoot>
              <Shortcut.ShortcutCommand className="bg-muted-foreground" />
              <Shortcut.ShortcutHotKey className="bg-muted-foreground">
                D
              </Shortcut.ShortcutHotKey>
            </Shortcut.ShortcutRoot>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
